import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api, SignInFlow } from '@dataroom/shared';

/**
 * Admin sign-in: password or emailed code, then mandatory MFA (ADR-0023). The
 * admin app is the only one with self-serve sign-up (/signup, the free trial), so
 * only it links there. The links never depend on whether an email has an account.
 *
 * While the service is LOCKED (ADR-0069: its keys need the company recovery
 * kit) sign-in cannot work, so the page says so and links to /unlock — a plain
 * link, a full page load, so the unlock page's strict CSP applies.
 */
export function LoginPage() {
  const navigate = useNavigate();
  // Only the locked server answers this (200); a running one says 404.
  const locked = useQuery({
    queryKey: ['recovery', 'status', 'login'],
    queryFn: () => api.get<{ keys: string }>('/recovery/status'),
    retry: false,
    staleTime: 60_000,
  });
  return (
    <SignInFlow
      title="ClosingVault Admin"
      subtitle="Sign in to manage your rooms."
      onSignedIn={() => navigate('/')}
      startSlot={
        <>
          {locked.data?.keys === 'locked' ? (
            <p role="alert" className="text-sm font-medium" data-testid="keys-locked-notice">
              ClosingVault is locked. A platform owner can unlock it at{' '}
              <a href="/unlock" className="underline">
                /unlock
              </a>
              .
            </p>
          ) : null}
          <p className="text-sm text-muted-foreground">
            New to ClosingVault?{' '}
            <Link to="/signup" className="font-medium text-foreground underline">
              Create an account
            </Link>
          </p>
        </>
      }
      codeSlot={
        <p className="text-xs text-muted-foreground">
          No code? If you don’t have an account yet,{' '}
          <Link to="/signup" className="underline">
            create one
          </Link>
          .
        </p>
      }
    />
  );
}
